import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Patient } from '../../../types/types';
import { UseDurationScheduleReturn } from './useDurationSchedule';
import { UseFormsAndRemindersReturn } from './useFormsAndReminders';
import { UseRecurrenceReturn } from './useRecurrence';
import { useRecurrenceDataGeneration } from './useRecurrenceDataGeneration';
import {
  useCreateAppointmentMutation,
  useCreateAppointmentSeriesMutation,
  Appointment as AppointmentResponse,
  AppointmentUpdate,
  SeriesResponse,
} from '@features/appointments';
import { ExpandableFieldItem } from '@features/appointment-types';
import { formatAppointmentCreatedNotification } from '../utils/appointmentNotifications';

interface UseAppointmentSubmissionProps {
  mode: 'create' | 'edit';
  selectedPatient?: Patient;
  selectedAppointmentType: UseFormsAndRemindersReturn['selectedAppointmentType'];
  durationSchedule: UseDurationScheduleReturn;
  recurrence: UseRecurrenceReturn;
  // Legacy fallback: templateId comes from templatesList?.[0]?.template_id in parent components
  // This is kept for backwards compatibility during the migration from template_id + encounter_type
  // to the new encounter_type_id system. In the future, this should be removed as
  // selectedAppointmentType.template_id should always have the correct value.
  templateId?: string | null;
  selectedForms?: ExpandableFieldItem[];
  selectedReminders?: ExpandableFieldItem[];
  telehealth?: boolean;
  notes?: string;
  hasDirtyChanges?: boolean;
  providerId?: string;

  // For edit mode
  appointment?: AppointmentResponse;
  onAppointmentUpdate?: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => Promise<void>;

  // For create mode
  onAppointmentCreated?: (appointment: AppointmentResponse | SeriesResponse) => void;
}

export interface UseAppointmentSubmissionReturn {
  isSubmitting: boolean;
  error: string | null;
  submit: () => Promise<void>;
  setError: (error: string | null) => void;
}

/**
 * Hook to handle appointment submission for both create and edit modes
 */
export const useAppointmentSubmission = ({
  mode,
  selectedPatient,
  selectedAppointmentType,
  durationSchedule,
  recurrence,
  templateId,
  selectedForms,
  selectedReminders,
  telehealth,
  notes,
  hasDirtyChanges = false,
  providerId,
  appointment,
  onAppointmentUpdate,
  onAppointmentCreated,
}: UseAppointmentSubmissionProps): UseAppointmentSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [createAppointment] = useCreateAppointmentMutation();
  const [createAppointmentSeries] = useCreateAppointmentSeriesMutation();
  const { generateRecurrenceData } = useRecurrenceDataGeneration({
    recurrence,
  });

  const submit = async (): Promise<void> => {
    try {
      const { startDateTime, duration } = durationSchedule.getAppointmentDateTime();

      if (!startDateTime || !duration) {
        setError('Invalid date or duration');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      if (mode === 'create') {
        await handleCreateSubmission(startDateTime, duration);
      } else {
        await handleEditSubmission(startDateTime, duration);
      }
    } catch (err) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} appointment:`, err);
      setError(
        `Failed to ${mode === 'create' ? 'create' : 'update'} appointment. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmission = async (startDateTime: Date, duration: number) => {
    if (!selectedPatient?.patient_id || !selectedAppointmentType?.encounter_type_id) {
      setError('Missing required fields');
      return;
    }

    // Convert forms to API format
    const formRules =
      selectedForms
        ?.map(form => ({
          form_definition_id: form.formId || '',
          offset: form.offset || '',
          title: form.name,
          id: form.ruleId,
        }))
        .filter(rule => rule.form_definition_id) || [];

    // Convert reminders to API format
    const reminderRules =
      selectedReminders?.map(reminder => ({
        reminder_template_id: reminder.name,
        offset: reminder.offset || '',
        title: reminder.name,
        id: reminder.ruleId,
      })) || [];

    // Create base appointment data
    const appointmentData = {
      patient_id: selectedPatient.patient_id,
      encounter_type_id: selectedAppointmentType.encounter_type_id,
      // Use template_id from the selected appointment type first, fall back to legacy templateId prop
      // The templateId prop is templatesList?.[0]?.template_id from parent components - a temporary fallback
      template_id: selectedAppointmentType.template_id || templateId || null,
      start_time: startDateTime.toISOString(),
      duration: duration,
      ...(providerId && { provider_id: providerId }),
      ...(telehealth !== undefined && { telehealth: telehealth }),
      ...(formRules.length > 0 && { form_rules: formRules }),
      ...(reminderRules.length > 0 && { reminder_rules: reminderRules }),
      ...(hasDirtyChanges && { override_defaults: true }),
      ...(notes ? { notes } : {}),
    };

    let createdAppointmentOrSeries: AppointmentResponse | SeriesResponse;

    // Generate recurrence rule if applicable
    const recurrenceData = generateRecurrenceData();
    if (recurrenceData) {
      // Create series data with our recurrence rule
      // For series, we need to use default_form_rules and default_reminder_rules
      const { form_rules, reminder_rules, override_defaults, provider_id, ...baseData } = appointmentData;
      const seriesData = {
        ...baseData,
        rrule: recurrenceData.rrule,
        end_date: recurrenceData.endDate,
        ...(provider_id && { provider_id }),
        ...(formRules.length > 0 && { default_form_rules: formRules }),
        ...(reminderRules.length > 0 && {
          default_reminder_rules: reminderRules,
        }),
        ...(hasDirtyChanges && { override_defaults: true }),
      };

      createdAppointmentOrSeries = await createAppointmentSeries({
        appointmentSeriesCreate: seriesData,
      }).unwrap();
    } else {
      // Create single appointment
      createdAppointmentOrSeries = await createAppointment({
        appointmentCreate: appointmentData,
      }).unwrap();
    }

    // Show success notification
    const notificationData = formatAppointmentCreatedNotification(createdAppointmentOrSeries);
    enqueueSnackbar('New Appointment Created!', {
      variant: 'success',
      data: notificationData,
    });

    // Call the callback if provided
    if (onAppointmentCreated) {
      onAppointmentCreated(createdAppointmentOrSeries);
    }
  };

  const handleEditSubmission = async (startDateTime: Date, duration: number) => {
    if (!appointment?.appointment_id || !onAppointmentUpdate) {
      setError('Missing appointment data or update handler');
      return;
    }

    // Prepare update data using the new model format
    const updateData: AppointmentUpdate = {
      start_time: startDateTime.toISOString(),
      duration: duration,
    };

    // Include patient_id if a patient is selected
    if (selectedPatient) {
      updateData.patient_id = selectedPatient.patient_id;
    }

    // Include encounter_type_id and template_id from selected appointment type
    if (selectedAppointmentType?.encounter_type_id) {
      updateData.encounter_type_id = selectedAppointmentType.encounter_type_id;
      // Use template_id from the selected appointment type first, fall back to legacy templateId prop
      // The templateId prop is templatesList?.[0]?.template_id from parent components - a temporary fallback
      // that ensures backwards compatibility during the migration period
      updateData.template_id = selectedAppointmentType.template_id || templateId || null;
    }

    // Convert forms to API format
    const formRules =
      selectedForms
        ?.map(form => ({
          form_definition_id: form.formId || '',
          offset: form.offset || '',
          title: form.name,
          id: form.ruleId,
        }))
        .filter(rule => rule.form_definition_id) || [];

    // Convert reminders to API format
    const reminderRules =
      selectedReminders?.map(reminder => ({
        reminder_template_id: reminder.name,
        offset: reminder.offset || '',
        title: reminder.name,
        id: reminder.ruleId,
      })) || [];

    // Include form and reminder rules if present
    if (formRules.length > 0) {
      updateData.form_rules = formRules;
    }
    if (reminderRules.length > 0) {
      updateData.reminder_rules = reminderRules;
    }

    // Include telehealth if provided
    if (telehealth !== undefined) {
      updateData.telehealth = telehealth;
    }

    // Include override_defaults if there are dirty changes
    if (hasDirtyChanges) {
      updateData.override_defaults = true;
    }

    // Include notes — send empty string to clear (backend ignores null on PATCH)
    if (notes !== undefined) {
      updateData.notes = notes || '';
    }

    // Generate recurrence rule if applicable
    const recurrenceData = generateRecurrenceData();
    if (recurrenceData) {
      updateData.rrule = recurrenceData.rrule;

      if (recurrenceData.endDate) {
        updateData.series_end_date = recurrenceData.endDate;
      }

      // If this appointment wasn't already part of a series, flag it to create a new series
      if (!appointment.series) {
        updateData.create_series = true;
      }
    }

    // Always use the onAppointmentUpdate handler with optional seriesId
    await onAppointmentUpdate(
      appointment.appointment_id,
      updateData,
      appointment.series?.series_id,
    );
  };

  return {
    isSubmitting,
    error,
    submit,
    setError,
  };
};
